import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { environment } from 'src/environments/environment';

/** The Microsoft Clarity project; empty outside production, which turns Clarity and its banner off. */
export const CLARITY_PROJECT_ID = new InjectionToken<string>('CLARITY_PROJECT_ID', {
  providedIn: 'root',
  factory: () => environment.clarityProjectId ?? '',
});

/** Where the visitor's choice is kept, in localStorage. */
export const COOKIE_CONSENT_KEY = 'bids.cookieConsent';

export type CookieChoice = 'accepted' | 'rejected';

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };
type ClarityWindow = Window & { clarity?: ClarityFn };

/**
 * Microsoft Clarity behind the cookie banner (ADO #270). Nothing is loaded before the visitor
 * clicks Accept: there is no tag in index.html, and the loader below is Microsoft's documented
 * snippet run only from accept() or, on a later visit, from start() when "accepted" is stored.
 * Reject after Accept tells Clarity to erase its cookies and stop (`consent`, false); the stored
 * refusal keeps the script from loading on later visits.
 */
@Injectable({ providedIn: 'root' })
export class ClarityConsentService {
  private readonly doc = inject(DOCUMENT);
  private readonly overlay = inject(OverlayContainer);
  private readonly projectId = inject(CLARITY_PROJECT_ID);

  /** Whether Clarity is configured at all; false on dev, where neither banner nor link shows. */
  readonly enabled = this.projectId !== '';
  readonly choice = signal<CookieChoice | null>(this.readChoice());
  readonly bannerOpen = signal(this.enabled && this.choice() === null);
  /** True when the banner was reopened from "Definições de cookies", so it takes the focus. */
  readonly reopened = signal(false);

  private loaded = false;

  /** At app start: loads Clarity when the visitor accepted on an earlier visit. */
  start(): void {
    if (this.enabled && this.choice() === 'accepted') {
      this.load();
    }
  }

  accept(): void {
    if (!this.enabled) return;
    this.saveChoice('accepted');
    this.close();
    this.load();
  }

  reject(): void {
    if (!this.enabled) return;
    this.saveChoice('rejected');
    this.close();
    if (this.loaded) {
      // Withdrawal: Clarity deletes its cookies and stops tracking until consent is given again.
      this.clarity()?.('consent', false);
    }
  }

  /** "Definições de cookies": shows the banner again so the visitor can change the choice. */
  openSettings(): void {
    if (!this.enabled) return;
    this.reopened.set(true);
    this.bannerOpen.set(true);
  }

  private close(): void {
    this.bannerOpen.set(false);
    this.reopened.set(false);
  }

  private load(): void {
    const win = this.win();
    if (!this.loaded) {
      this.loaded = true;
      // Dialogs, menus and tooltips render outside the pages, in the CDK overlay container; they
      // show order, patient and pharmacy data, so the whole container is masked.
      this.overlay.getContainerElement().setAttribute('data-clarity-mask', 'true');

      // Microsoft's Clarity loader: a queue on window.clarity, then the tag script.
      win.clarity =
        win.clarity ||
        function () {
          // eslint-disable-next-line prefer-rest-params
          (win.clarity!.q = win.clarity!.q || []).push(arguments);
        };
      const script = this.doc.createElement('script');
      script.async = true;
      script.src = 'https://www.clarity.ms/tag/' + this.projectId;
      script.setAttribute('data-clarity-loader', '');
      this.doc.head.appendChild(script);
    }
    win.clarity?.('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
  }

  private clarity(): ClarityFn | undefined {
    return this.win().clarity;
  }

  private win(): ClarityWindow {
    return (this.doc.defaultView ?? window) as ClarityWindow;
  }

  private readChoice(): CookieChoice | null {
    try {
      const value = this.win().localStorage.getItem(COOKIE_CONSENT_KEY);
      return value === 'accepted' || value === 'rejected' ? value : null;
    } catch {
      return null;
    }
  }

  private saveChoice(choice: CookieChoice): void {
    this.choice.set(choice);
    try {
      this.win().localStorage.setItem(COOKIE_CONSENT_KEY, choice);
    } catch {
      // Storage blocked: the choice holds for this page only and the banner returns next visit.
    }
  }
}
